import { TEMPORARILY_UNAVAILABLE_COUNTRIES } from '@/constants/paymentMethods'

import { CpnCountryCode, CpnPaymentMethod, CpnPaymentReason } from './cpn'
import { logger } from './logger'

export type CountryCode = string
export type CurrencyCode = string

export type PaymentPreference = {
  receiveCountry: CountryCode
  receiveCurrency: CurrencyCode
  paymentMethod: CpnPaymentMethod
  lastUsedAt: string
}

const STORAGE_KEY_PREFIX = 'payment_preference_'
const DEFAULT_PREFERENCE: Omit<PaymentPreference, 'lastUsedAt'> = {
  receiveCountry: 'NG',
  receiveCurrency: 'NGN',
  paymentMethod: 'BANK-TRANSFER' as CpnPaymentMethod,
}

export const DEFAULT_PAYMENT_REASON: CpnPaymentReason = 'PMT022' // Remittance payments to friends/family

const getStorageKey = (userId: string | undefined): string | null => {
  if (!userId) return null
  return `${STORAGE_KEY_PREFIX}${userId}`
}

export const savePaymentPreference = (
  userId: string | undefined,
  preference: Omit<PaymentPreference, 'lastUsedAt'>
): void => {
  try {
    const storageKey = getStorageKey(userId)
    if (!storageKey) {
      logger.warn('Cannot save payment preference: No user ID provided')
      return
    }

    const preferenceToSave: PaymentPreference = {
      ...preference,
      lastUsedAt: new Date().toISOString(),
    }

    localStorage.setItem(storageKey, JSON.stringify(preferenceToSave))
  } catch (error) {
    logger.error('Failed to save payment preference', error)
  }
}

export const getPaymentPreference = (
  userId: string | undefined
): Omit<PaymentPreference, 'lastUsedAt'> => {
  try {
    const storageKey = getStorageKey(userId)
    if (!storageKey) return DEFAULT_PREFERENCE

    const stored = localStorage.getItem(storageKey)
    if (!stored) return DEFAULT_PREFERENCE

    const parsed: PaymentPreference = JSON.parse(stored)

    if (
      !parsed.receiveCountry ||
      !parsed.receiveCurrency ||
      !parsed.paymentMethod
    ) {
      logger.warn('Invalid payment preference data, using defaults')
      return DEFAULT_PREFERENCE
    }

    if (
      TEMPORARILY_UNAVAILABLE_COUNTRIES.includes(
        parsed.receiveCountry as CpnCountryCode
      )
    ) {
      return DEFAULT_PREFERENCE
    }

    return {
      receiveCountry: parsed.receiveCountry,
      receiveCurrency: parsed.receiveCurrency,
      paymentMethod: parsed.paymentMethod,
    }
  } catch (error) {
    logger.error('Failed to retrieve payment preference', error)
    return DEFAULT_PREFERENCE
  }
}

export const clearPaymentPreference = (userId: string | undefined): void => {
  try {
    const storageKey = getStorageKey(userId)
    if (!storageKey) return

    localStorage.removeItem(storageKey)
  } catch (error) {
    logger.error('Failed to clear payment preference', error)
  }
}

export const hasPaymentPreference = (userId: string | undefined): boolean => {
  try {
    const storageKey = getStorageKey(userId)
    if (!storageKey) return false

    return localStorage.getItem(storageKey) !== null
  } catch (error) {
    logger.error('Failed to check payment preference', error)
    return false
  }
}
